import React, { useState, useCallback, useMemo } from 'react';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { Component, Project } from '../types';
import { authService } from '../services/auth';
import { useBookmarks } from '../contexts/BookmarkContext';

interface BookmarkButtonProps {
  component: Component;
  project?: Project;
  size?: 'sm' | 'md';
  showLabel?: boolean;
}

const BookmarkButton: React.FC<BookmarkButtonProps> = React.memo(({ 
  component, 
  project,
  size = 'md',
  showLabel = false 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isBookmarked, addBookmark, removeBookmark } = useBookmarks();

  // Get bookmark status from context - NO API CALLS!
  const bookmarked = isBookmarked(component.name);

  const handleToggleBookmark = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      alert('Please sign in to bookmark components');
      return;
    }

    setIsLoading(true);
    try {
      if (bookmarked) {
        await removeBookmark(component.name);
      } else {
        await addBookmark(component, project);
      }
    } catch (error) {
      console.error('Bookmark operation failed:', error);
      // Show user-friendly error message
      if (error instanceof Error) {
        if (error.message.includes('must belong to a project')) {
          alert('Cannot bookmark this component: missing project information. Please contact support.');
        } else {
          alert(`Failed to bookmark component: ${error.message}`);
        }
      } else {
        alert('Failed to bookmark component. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [bookmarked, component.name, project, addBookmark, removeBookmark]);

  const sizeClasses = useMemo(() => ({
    sm: 'w-6 h-6',
    md: 'w-5 h-5'
  }), []);

  const buttonClasses = useMemo(() => ({
    sm: 'p-1',
    md: 'p-2'
  }), []);

  return (
    <button
      onClick={handleToggleBookmark}
      disabled={isLoading}
      className={`flex items-center justify-center rounded-lg transition-colors ${
        bookmarked 
          ? 'text-yellow-600 hover:text-yellow-700' 
          : 'text-gray-400 hover:text-gray-600'
      } ${buttonClasses[size]}`}
      title={bookmarked ? 'Remove bookmark' : 'Add bookmark'}
    >
      {bookmarked ? (
        <BookmarkCheck className={sizeClasses[size]} />
      ) : (
        <Bookmark className={sizeClasses[size]} />
      )}
      
      {showLabel && (
        <span className="text-xs text-gray-500 ml-1">
          {bookmarked ? 'Bookmarked' : 'Bookmark'}
        </span>
      )}
    </button>
  );
});

BookmarkButton.displayName = 'BookmarkButton';

export default BookmarkButton;
